import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { resolveApiBaseUrl } from '../../../utils/apiClient';

// Types
interface Complaint {
  id: string;
  subject: string;
  subjectAr?: string;
  description: string;
  descriptionAr?: string;
  category: ComplaintCategory;
  priority: ComplaintPriority;
  status: ComplaintStatus;
  createdAt: string;
  updatedAt: string;
  reviewedAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  assignedAt?: string;
  resolutionNotes?: string;
  resolutionNotesAr?: string;
  rejectionReason?: string;
  internalNotes?: string;
  satisfactionRating?: number;
  satisfactionComment?: string;
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    phone?: string;
  };
  assignedTo?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
  property?: {
    id: string;
    title: string;
    titleAr?: string;
  };
  agent?: {
    id: string;
    name: string;
    nameAr?: string;
  };
  company?: {
    id: string;
    name: string;
    nameAr?: string;
  };
  updates?: ComplaintUpdate[];
  _count?: {
    attachments: number;
    updates: number;
  };
}

interface ComplaintUpdate {
  id: string;
  fromStatus?: ComplaintStatus;
  toStatus: ComplaintStatus;
  message: string;
  messageAr?: string;
  isPublic: boolean;
  createdAt: string;
}

interface AdminUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

interface Stats {
  total: number;
  byStatus: Record<string, number>;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
  avgResolutionTimeHours: number;
  avgSatisfactionRating: number;
}

type ComplaintCategory = 'SERVICE_QUALITY' | 'AGENT_BEHAVIOR' | 'PROPERTY_ISSUES' | 'PAYMENT_BILLING' | 'TECHNICAL_ISSUES' | 'OTHER';
type ComplaintPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
type ComplaintStatus = 'NEW' | 'UNDER_REVIEW' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | 'REJECTED';

// Status and priority colors
const statusColors: Record<string, string> = {
  NEW: 'bg-blue-100 text-blue-700 border-blue-200',
  UNDER_REVIEW: 'bg-amber-100 text-amber-700 border-amber-200',
  IN_PROGRESS: 'bg-purple-100 text-purple-700 border-purple-200',
  RESOLVED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  CLOSED: 'bg-gray-100 text-gray-600 border-gray-200',
  REJECTED: 'bg-rose-100 text-rose-700 border-rose-200'
};

const priorityColors: Record<string, string> = {
  LOW: 'bg-gray-100 text-gray-600 border-gray-200',
  MEDIUM: 'bg-blue-100 text-blue-700 border-blue-200',
  HIGH: 'bg-amber-100 text-amber-700 border-amber-200',
  URGENT: 'bg-rose-100 text-rose-700 border-rose-200'
};

const categoryIcons: Record<string, string> = {
  SERVICE_QUALITY: 'ri-customer-service-2-line',
  AGENT_BEHAVIOR: 'ri-user-voice-line',
  PROPERTY_ISSUES: 'ri-building-2-line',
  PAYMENT_BILLING: 'ri-money-dollar-circle-line',
  TECHNICAL_ISSUES: 'ri-bug-line',
  OTHER: 'ri-question-line'
};

const categoryLabels: Record<string, { en: string; ar: string }> = {
  SERVICE_QUALITY: { en: 'Service Quality', ar: 'جودة الخدمة' },
  AGENT_BEHAVIOR: { en: 'Agent Behavior', ar: 'سلوك الوكيل' },
  PROPERTY_ISSUES: { en: 'Property Issues', ar: 'مشاكل العقار' },
  PAYMENT_BILLING: { en: 'Payment & Billing', ar: 'الدفع والفواتير' },
  TECHNICAL_ISSUES: { en: 'Technical Issues', ar: 'مشاكل تقنية' },
  OTHER: { en: 'Other', ar: 'أخرى' }
};

const statusLabels: Record<string, { en: string; ar: string }> = {
  NEW: { en: 'New', ar: 'جديد' },
  UNDER_REVIEW: { en: 'Under Review', ar: 'قيد المراجعة' },
  IN_PROGRESS: { en: 'In Progress', ar: 'قيد التنفيذ' },
  RESOLVED: { en: 'Resolved', ar: 'تم الحل' },
  CLOSED: { en: 'Closed', ar: 'مغلق' },
  REJECTED: { en: 'Rejected', ar: 'مرفوض' }
};

const priorityLabels: Record<string, { en: string; ar: string }> = {
  LOW: { en: 'Low', ar: 'منخفضة' },
  MEDIUM: { en: 'Medium', ar: 'متوسطة' },
  HIGH: { en: 'High', ar: 'عالية' },
  URGENT: { en: 'Urgent', ar: 'عاجلة' }
};

const API_BASE = resolveApiBaseUrl();

export default function AdminComplaintsPage() {
  const { session } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '');
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || '');
  const [assigneeFilter, setAssigneeFilter] = useState(searchParams.get('assignee') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));

  // Modal state
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch complaints
  const fetchComplaints = useCallback(async () => {
    if (!session?.token) return;
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('page', currentPage.toString());
      params.set('pageSize', '20');
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (priorityFilter) params.set('priority', priorityFilter);
      if (assigneeFilter) params.set('assignedToId', assigneeFilter);

      const response = await fetch(`${API_BASE}/complaints/admin?${params}`, {
        headers: { Authorization: `Bearer ${session?.token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch complaints');

      const data = await response.json();
      setComplaints(data.complaints);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [session?.token, currentPage, debouncedSearch, statusFilter, categoryFilter, priorityFilter, assigneeFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!session?.token) return;

    try {
      const response = await fetch(`${API_BASE}/complaints/admin/stats`, {
        headers: { Authorization: `Bearer ${session?.token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [session?.token]);

  // Fetch admins for assignment
  const fetchAdmins = useCallback(async () => {
    if (!session?.token) return;

    try {
      const response = await fetch(`${API_BASE}/complaints/admin/admins`, {
        headers: { Authorization: `Bearer ${session?.token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch admins');

      const data = await response.json();
      setAdmins(data);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    }
  }, [session?.token]);

  // Initial load
  useEffect(() => {
    fetchComplaints();
    fetchStats();
    fetchAdmins();
  }, [fetchComplaints, fetchStats, fetchAdmins]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter) params.set('status', statusFilter);
    if (categoryFilter) params.set('category', categoryFilter);
    if (priorityFilter) params.set('priority', priorityFilter);
    if (assigneeFilter) params.set('assignee', assigneeFilter);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params);
  }, [debouncedSearch, statusFilter, categoryFilter, priorityFilter, assigneeFilter, currentPage, setSearchParams]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCategoryFilter('');
    setPriorityFilter('');
    setAssigneeFilter('');
    setCurrentPage(1);
  };

  const hasFilters = search || statusFilter || categoryFilter || priorityFilter || assigneeFilter;

  const handleComplaintUpdate = () => {
    fetchComplaints();
    fetchStats();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#004D40] shadow-lg shadow-[#004D40]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/admin"
                className="flex items-center justify-center w-11 h-11 rounded-2xl bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all duration-300 hover:scale-105"
              >
                <i className="ri-arrow-left-line text-white text-lg"></i>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white">Complaints Management</h1>
                <p className="text-[#B7DDD7] mt-1 flex items-center gap-2">
                  <i className="ri-feedback-line"></i>
                  {pagination.total} total complaints
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-2xl text-white/90 text-sm">
                <i className="ri-shield-check-line mr-2"></i>
                Admin Panel
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <StatsCard
              label="Total"
              value={stats.total}
              icon="ri-file-list-3-line"
              bgColor="bg-[var(--pv-color-surface-sunken)]"
              textColor="text-[#003930]"
            />
            <StatsCard
              label="New"
              value={stats.byStatus['NEW'] || 0}
              icon="ri-add-circle-line"
              bgColor="bg-blue-50"
              textColor="text-blue-600"
            />
            <StatsCard
              label="Under Review"
              value={stats.byStatus['UNDER_REVIEW'] || 0}
              icon="ri-search-eye-line"
              bgColor="bg-amber-50"
              textColor="text-amber-600"
            />
            <StatsCard
              label="In Progress"
              value={stats.byStatus['IN_PROGRESS'] || 0}
              icon="ri-loader-4-line"
              bgColor="bg-purple-50"
              textColor="text-purple-600"
            />
            <StatsCard
              label="Resolved"
              value={stats.byStatus['RESOLVED'] || 0}
              icon="ri-checkbox-circle-line"
              bgColor="bg-emerald-50"
              textColor="text-emerald-600"
            />
            <StatsCard
              label="Avg. Rating"
              value={stats.avgSatisfactionRating?.toFixed(1) || 'N/A'}
              icon="ri-star-line"
              bgColor="bg-yellow-50"
              textColor="text-yellow-600"
            />
          </div>
        )}

        {/* Filters Card */}
        <div className="bg-white rounded-3xl shadow-xl shadow-[#004D40]/5 border border-[var(--pv-border-soft)]/50 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-[var(--pv-color-surface-sunken)] rounded-xl flex items-center justify-center">
                  <i className="ri-search-line text-[#004D40]"></i>
                </div>
                <input
                  type="text"
                  placeholder="Search by subject or user..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-14 pr-4 py-4 border-2 border-[var(--pv-border-soft)] rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004D40]/20 focus:border-[#1B7566] transition-all duration-300 bg-[var(--pv-color-surface-sunken)]/30"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full lg:w-40">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-4 border-2 border-[var(--pv-border-soft)] rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004D40]/20 focus:border-[#1B7566] bg-white appearance-none cursor-pointer transition-all duration-300"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239333ea'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
              >
                <option value="">All Status</option>
                {Object.keys(statusLabels).map((status) => (
                  <option key={status} value={status}>{statusLabels[status].en}</option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div className="w-full lg:w-44">
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-4 border-2 border-[var(--pv-border-soft)] rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004D40]/20 focus:border-[#1B7566] bg-white appearance-none cursor-pointer transition-all duration-300"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239333ea'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
              >
                <option value="">All Categories</option>
                {Object.keys(categoryLabels).map((cat) => (
                  <option key={cat} value={cat}>{categoryLabels[cat].en}</option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div className="w-full lg:w-36">
              <select
                value={priorityFilter}
                onChange={(e) => {
                  setPriorityFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-4 border-2 border-[var(--pv-border-soft)] rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004D40]/20 focus:border-[#1B7566] bg-white appearance-none cursor-pointer transition-all duration-300"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239333ea'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
              >
                <option value="">All Priority</option>
                {Object.keys(priorityLabels).map((pri) => (
                  <option key={pri} value={pri}>{priorityLabels[pri].en}</option>
                ))}
              </select>
            </div>

            {/* Assignee Filter */}
            <div className="w-full lg:w-44">
              <select
                value={assigneeFilter}
                onChange={(e) => {
                  setAssigneeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-4 border-2 border-[var(--pv-border-soft)] rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004D40]/20 focus:border-[#1B7566] bg-white appearance-none cursor-pointer transition-all duration-300"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239333ea'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
              >
                <option value="">All Assignees</option>
                <option value="unassigned">Unassigned</option>
                {admins.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.firstName || admin.lastName ? `${admin.firstName || ''} ${admin.lastName || ''}`.trim() : admin.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="px-5 py-4 text-[#004D40] hover:text-[#003930] bg-[var(--pv-color-surface-sunken)] hover:bg-[#DDEEEB] rounded-2xl transition-all duration-300 font-medium"
              >
                <i className="ri-close-line mr-1"></i>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-5 bg-rose-50 border-2 border-rose-200 rounded-2xl text-rose-700 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <i className="ri-error-warning-fill text-xl"></i>
              {error}
            </span>
            <button onClick={() => setError(null)} className="p-2 hover:bg-rose-100 rounded-xl transition-colors">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        )}

        {/* Complaints Table */}
        <div className="bg-white rounded-3xl shadow-xl shadow-[#004D40]/5 border border-[var(--pv-border-soft)]/50 overflow-hidden">
          {loading ? (
            <div className="p-8">
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-[var(--pv-color-surface-sunken)]/50 animate-pulse">
                    <div className="w-12 h-12 bg-[#DDEEEB] rounded-2xl"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-[#DDEEEB] rounded-full w-1/4"></div>
                      <div className="h-3 bg-[var(--pv-color-surface-sunken)] rounded-full w-1/3"></div>
                    </div>
                    <div className="h-8 bg-[#DDEEEB] rounded-full w-24"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : complaints.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-20 h-20 bg-[var(--pv-color-surface-sunken)] rounded-3xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-feedback-line text-4xl text-[#1B7566]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No complaints found</h3>
              <p className="text-gray-500">
                {hasFilters ? 'Try adjusting your filters' : 'No complaints have been submitted yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--pv-color-surface-sunken)] border-b-2 border-[var(--pv-border-soft)]">
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Complaint
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      User
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Category
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Date
                    </th>
                    <th className="text-right px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F2F8F7]">
                  {complaints.map((complaint) => (
                    <ComplaintRow
                      key={complaint.id}
                      complaint={complaint}
                      onViewDetails={() => setSelectedComplaint(complaint)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-5 bg-[var(--pv-color-surface-sunken)]/50 border-t-2 border-[var(--pv-border-soft)] flex items-center justify-between">
              <div className="text-sm text-[#004D40] font-medium">
                Showing {((currentPage - 1) * pagination.pageSize) + 1} to{' '}
                {Math.min(currentPage * pagination.pageSize, pagination.total)} of{' '}
                {pagination.total} complaints
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-10 h-10 flex items-center justify-center border-2 border-[var(--pv-border-soft)] rounded-xl hover:bg-[var(--pv-color-surface-sunken)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <i className="ri-arrow-left-s-line text-[#004D40]"></i>
                </button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let page: number;
                  if (pagination.totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= pagination.totalPages - 2) {
                    page = pagination.totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 flex items-center justify-center rounded-xl font-medium transition-all duration-300 ${
                        currentPage === page
                          ? 'bg-[#004D40] text-white shadow-lg shadow-[#004D40]/30'
                          : 'border-2 border-[var(--pv-border-soft)] text-[#004D40] hover:bg-[var(--pv-color-surface-sunken)]'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                {pagination.totalPages > 5 && currentPage < pagination.totalPages - 2 && (
                  <span className="text-[#1B7566] px-2">...</span>
                )}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className="w-10 h-10 flex items-center justify-center border-2 border-[var(--pv-border-soft)] rounded-xl hover:bg-[var(--pv-color-surface-sunken)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <i className="ri-arrow-right-s-line text-[#004D40]"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Complaint Details Modal */}
      {selectedComplaint && (
        <ComplaintDetailsModal
          complaint={selectedComplaint}
          admins={admins}
          token={session?.token || ''}
          onClose={() => setSelectedComplaint(null)}
          onUpdate={handleComplaintUpdate}
        />
      )}
    </div>
  );
}

// Stats Card Component
function StatsCard({ label, value, icon, bgColor, textColor }: {
  label: string;
  value: number | string;
  icon: string;
  bgColor: string;
  textColor: string;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-[#004D40]/5 border border-[var(--pv-border-soft)]/50 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
          <i className={`${icon} text-xl ${textColor}`}></i>
        </div>
        <div>
          <div className={`text-2xl font-bold ${textColor}`}>{value}</div>
          <div className="text-xs text-gray-500 font-medium">{label}</div>
        </div>
      </div>
    </div>
  );
}

// Complaint Row Component
function ComplaintRow({ complaint, onViewDetails }: { complaint: Complaint; onViewDetails: () => void }) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUserName = () => {
    const { firstName, lastName, email } = complaint.user;
    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim();
    }
    return email;
  };

  const getAssigneeName = () => {
    if (!complaint.assignedTo) return null;
    const { firstName, lastName, email } = complaint.assignedTo;
    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim();
    }
    return email;
  };

  return (
    <tr className="hover:bg-[var(--pv-color-surface-sunken)]/50 transition-all duration-300 group">
      <td className="px-6 py-5">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
            priorityColors[complaint.priority].split(' ')[0]
          } group-hover:scale-105 transition-transform duration-300`}>
            <i className={`${categoryIcons[complaint.category]} text-xl ${
              priorityColors[complaint.priority].split(' ')[1]
            }`}></i>
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate max-w-[200px]">{complaint.subject}</div>
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <span>#{complaint.id.slice(0, 8)}</span>
              {complaint._count && complaint._count.attachments > 0 && (
                <span className="flex items-center gap-1 text-[#004D40]">
                  <i className="ri-attachment-line"></i>
                  {complaint._count.attachments}
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-5">
        <div className="text-sm">
          <div className="font-medium text-gray-900">{getUserName()}</div>
          <div className="text-gray-500">{complaint.user.email}</div>
        </div>
      </td>
      <td className="px-6 py-5">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--pv-color-surface-sunken)] text-[#003930]">
          <i className={categoryIcons[complaint.category]}></i>
          {categoryLabels[complaint.category]?.en || complaint.category}
        </span>
      </td>
      <td className="px-6 py-5">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${priorityColors[complaint.priority]}`}>
          {complaint.priority === 'URGENT' && <span className="w-2 h-2 bg-rose-500 rounded-full animate-pulse"></span>}
          {priorityLabels[complaint.priority]?.en || complaint.priority}
        </span>
      </td>
      <td className="px-6 py-5">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${statusColors[complaint.status]}`}>
          {complaint.status === 'NEW' && <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>}
          {statusLabels[complaint.status]?.en || complaint.status}
        </span>
      </td>
      <td className="px-6 py-5">
        {getAssigneeName() ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
            <i className="ri-user-line"></i>
            {getAssigneeName()}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            Unassigned
          </span>
        )}
      </td>
      <td className="px-6 py-5 text-sm text-gray-500 font-medium">
        {formatDate(complaint.createdAt)}
      </td>
      <td className="px-6 py-5 text-right">
        <button
          onClick={onViewDetails}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--pv-color-surface-sunken)] hover:bg-[#DDEEEB] text-[#003930] rounded-xl transition-all duration-300 font-medium text-sm hover:scale-105"
        >
          <i className="ri-eye-line"></i>
          View
        </button>
      </td>
    </tr>
  );
}

// Complaint Details Modal
interface ComplaintDetailsModalProps {
  complaint: Complaint;
  admins: AdminUser[];
  token: string;
  onClose: () => void;
  onUpdate: () => void;
}

function ComplaintDetailsModal({ complaint, admins, token, onClose, onUpdate }: ComplaintDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'timeline' | 'actions'>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Action form state
  const [newStatus, setNewStatus] = useState(complaint.status);
  const [newPriority, setNewPriority] = useState(complaint.priority);
  const [assigneeId, setAssigneeId] = useState(complaint.assignedTo?.id || '');
  const [updateMessage, setUpdateMessage] = useState('');
  const [isPublicUpdate, setIsPublicUpdate] = useState(true);
  const [internalNotes, setInternalNotes] = useState(complaint.internalNotes || '');
  const [resolutionNotes, setResolutionNotes] = useState(complaint.resolutionNotes || '');
  const [rejectionReason, setRejectionReason] = useState(complaint.rejectionReason || '');

  const API_BASE = resolveApiBaseUrl();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserName = () => {
    const { firstName, lastName, email } = complaint.user;
    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim();
    }
    return email;
  };

  const handleAssign = async () => {
    if (!assigneeId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/complaints/admin/${complaint.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ adminId: assigneeId })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to assign complaint');
      }

      onUpdate();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Update complaint
      const updateData: Record<string, unknown> = {
        status: newStatus,
        priority: newPriority,
        internalNotes: internalNotes || undefined
      };

      if (newStatus === 'RESOLVED') {
        updateData.resolutionNotes = resolutionNotes;
      }
      if (newStatus === 'REJECTED') {
        updateData.rejectionReason = rejectionReason;
      }

      const response = await fetch(`${API_BASE}/complaints/admin/${complaint.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update complaint');
      }

      // Add status update message if provided
      if (updateMessage.trim()) {
        await fetch(`${API_BASE}/complaints/admin/${complaint.id}/update`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            message: updateMessage,
            isPublic: isPublicUpdate,
            toStatus: newStatus
          })
        });
      }

      onUpdate();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-[#004D40] p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                priorityColors[complaint.priority].split(' ')[0]
              }`}>
                <i className={`${categoryIcons[complaint.category]} text-2xl ${
                  priorityColors[complaint.priority].split(' ')[1]
                }`}></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{complaint.subject}</h2>
                <p className="text-white/80 text-sm flex items-center gap-2 mt-1">
                  <span>#{complaint.id.slice(0, 8)}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${statusColors[complaint.status]}`}>
                    {statusLabels[complaint.status]?.en}
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <i className="ri-close-line text-white text-xl"></i>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            {(['details', 'timeline', 'actions'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab
                    ? 'bg-white text-[#003930]'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {tab === 'details' && <i className="ri-file-text-line mr-2"></i>}
                {tab === 'timeline' && <i className="ri-time-line mr-2"></i>}
                {tab === 'actions' && <i className="ri-settings-3-line mr-2"></i>}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm">
              {error}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Description */}
              <div className="bg-[var(--pv-color-surface-sunken)] rounded-2xl p-5 border border-[var(--pv-border-soft)]">
                <h3 className="font-semibold text-[#003930] mb-3 flex items-center gap-2">
                  <i className="ri-file-text-line"></i>
                  Description
                </h3>
                <p className="text-gray-700 whitespace-pre-wrap">{complaint.description}</p>
              </div>

              {/* User Info */}
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <i className="ri-user-line"></i>
                  Submitted By
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Name</p>
                    <p className="font-medium text-gray-900">{getUserName()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Email</p>
                    <p className="font-medium text-gray-900">{complaint.user.email}</p>
                  </div>
                  {complaint.user.phone && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Phone</p>
                      <p className="font-medium text-gray-900">{complaint.user.phone}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Linked Entities */}
              {(complaint.property || complaint.agent || complaint.company) && (
                <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
                  <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                    <i className="ri-links-line"></i>
                    Linked Entities
                  </h3>
                  <div className="space-y-3">
                    {complaint.property && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                          <i className="ri-building-2-line text-amber-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-amber-600 uppercase tracking-wide">Property</p>
                          <p className="font-medium text-amber-900">{complaint.property.title}</p>
                        </div>
                      </div>
                    )}
                    {complaint.agent && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                          <i className="ri-user-star-line text-amber-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-amber-600 uppercase tracking-wide">Agent</p>
                          <p className="font-medium text-amber-900">{complaint.agent.name}</p>
                        </div>
                      </div>
                    )}
                    {complaint.company && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                          <i className="ri-building-4-line text-amber-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-amber-600 uppercase tracking-wide">Company</p>
                          <p className="font-medium text-amber-900">{complaint.company.name}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-2xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Category</p>
                  <p className="font-semibold text-gray-900 flex items-center gap-2">
                    <i className={categoryIcons[complaint.category]}></i>
                    {categoryLabels[complaint.category]?.en}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Priority</p>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${priorityColors[complaint.priority]}`}>
                    {priorityLabels[complaint.priority]?.en}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-2xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Created</p>
                  <p className="font-medium text-gray-900 text-sm">{formatDate(complaint.createdAt)}</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Assigned To</p>
                  <p className="font-medium text-gray-900">
                    {complaint.assignedTo
                      ? `${complaint.assignedTo.firstName || ''} ${complaint.assignedTo.lastName || ''}`.trim() || complaint.assignedTo.email
                      : 'Unassigned'
                    }
                  </p>
                </div>
              </div>

              {/* Resolution/Rejection Notes */}
              {complaint.resolutionNotes && (
                <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-200">
                  <h3 className="font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    Resolution Notes
                  </h3>
                  <p className="text-emerald-800 whitespace-pre-wrap">{complaint.resolutionNotes}</p>
                </div>
              )}

              {complaint.rejectionReason && (
                <div className="bg-rose-50 rounded-2xl p-5 border border-rose-200">
                  <h3 className="font-semibold text-rose-700 mb-3 flex items-center gap-2">
                    <i className="ri-close-circle-line"></i>
                    Rejection Reason
                  </h3>
                  <p className="text-rose-800 whitespace-pre-wrap">{complaint.rejectionReason}</p>
                </div>
              )}

              {/* Satisfaction Rating */}
              {complaint.satisfactionRating && (
                <div className="bg-yellow-50 rounded-2xl p-5 border border-yellow-200">
                  <h3 className="font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                    <i className="ri-star-line"></i>
                    User Satisfaction
                  </h3>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`text-2xl ${
                          star <= complaint.satisfactionRating!
                            ? 'ri-star-fill text-yellow-500'
                            : 'ri-star-line text-yellow-300'
                        }`}
                      ></i>
                    ))}
                    <span className="ml-2 text-yellow-700 font-medium">
                      {complaint.satisfactionRating}/5
                    </span>
                  </div>
                  {complaint.satisfactionComment && (
                    <p className="mt-3 text-yellow-800">{complaint.satisfactionComment}</p>
                  )}
                </div>
              )}

              {/* Internal Notes (Admin Only) */}
              {complaint.internalNotes && (
                <div className="bg-slate-100 rounded-2xl p-5 border border-slate-300">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <i className="ri-lock-line"></i>
                    Internal Notes (Admin Only)
                  </h3>
                  <p className="text-slate-800 whitespace-pre-wrap">{complaint.internalNotes}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-4">
              {/* Creation */}
              <TimelineItem
                icon="ri-add-circle-line"
                iconBg="bg-blue-100"
                iconColor="text-blue-600"
                title="Complaint Created"
                date={complaint.createdAt}
                isPublic
              />

              {/* Updates */}
              {complaint.updates?.map((update) => (
                <TimelineItem
                  key={update.id}
                  icon={update.toStatus === 'RESOLVED' ? 'ri-checkbox-circle-line' : update.toStatus === 'REJECTED' ? 'ri-close-circle-line' : 'ri-arrow-right-line'}
                  iconBg={statusColors[update.toStatus].split(' ')[0]}
                  iconColor={statusColors[update.toStatus].split(' ')[1]}
                  title={`Status changed to ${statusLabels[update.toStatus]?.en}`}
                  subtitle={update.message}
                  date={update.createdAt}
                  isPublic={update.isPublic}
                />
              ))}

              {/* Assigned */}
              {complaint.assignedAt && (
                <TimelineItem
                  icon="ri-user-add-line"
                  iconBg="bg-purple-100"
                  iconColor="text-purple-600"
                  title={`Assigned to ${complaint.assignedTo?.firstName || complaint.assignedTo?.email}`}
                  date={complaint.assignedAt}
                  isPublic
                />
              )}

              {/* Reviewed */}
              {complaint.reviewedAt && (
                <TimelineItem
                  icon="ri-search-eye-line"
                  iconBg="bg-amber-100"
                  iconColor="text-amber-600"
                  title="Complaint Reviewed"
                  date={complaint.reviewedAt}
                  isPublic
                />
              )}

              {/* Resolved */}
              {complaint.resolvedAt && (
                <TimelineItem
                  icon="ri-checkbox-circle-line"
                  iconBg="bg-emerald-100"
                  iconColor="text-emerald-600"
                  title="Complaint Resolved"
                  date={complaint.resolvedAt}
                  isPublic
                />
              )}

              {/* Closed */}
              {complaint.closedAt && (
                <TimelineItem
                  icon="ri-folder-close-line"
                  iconBg="bg-gray-100"
                  iconColor="text-gray-600"
                  title="Complaint Closed"
                  date={complaint.closedAt}
                  isPublic
                />
              )}

              {(!complaint.updates || complaint.updates.length === 0) && !complaint.assignedAt && (
                <div className="text-center py-8 text-gray-500">
                  <i className="ri-time-line text-4xl text-gray-300 mb-2"></i>
                  <p>No timeline updates yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-6">
              {/* Assignment */}
              <div className="bg-[var(--pv-color-surface-sunken)] rounded-2xl p-5 border border-[var(--pv-border-soft)]">
                <h3 className="font-semibold text-[#003930] mb-4 flex items-center gap-2">
                  <i className="ri-user-add-line"></i>
                  Assign Complaint
                </h3>
                <div className="flex gap-3">
                  <select
                    value={assigneeId}
                    onChange={(e) => setAssigneeId(e.target.value)}
                    className="flex-1 px-4 py-3 border-2 border-[var(--pv-border-soft)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-[#004D40] bg-white"
                  >
                    <option value="">Select admin...</option>
                    {admins.map((admin) => (
                      <option key={admin.id} value={admin.id}>
                        {admin.firstName || admin.lastName
                          ? `${admin.firstName || ''} ${admin.lastName || ''}`.trim()
                          : admin.email
                        }
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAssign}
                    disabled={!assigneeId || isSubmitting}
                    className="px-6 py-3 bg-[#004D40] text-white rounded-xl font-medium hover:bg-[#003930] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Assign
                  </button>
                </div>
              </div>

              {/* Status & Priority Update */}
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
                <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <i className="ri-settings-3-line"></i>
                  Update Status & Priority
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value as ComplaintStatus)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-[#004D40]"
                      >
                        {Object.keys(statusLabels).map((status) => (
                          <option key={status} value={status}>{statusLabels[status].en}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                      <select
                        value={newPriority}
                        onChange={(e) => setNewPriority(e.target.value as ComplaintPriority)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-[#004D40]"
                      >
                        {Object.keys(priorityLabels).map((priority) => (
                          <option key={priority} value={priority}>{priorityLabels[priority].en}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Resolution Notes (shown when resolved) */}
                  {newStatus === 'RESOLVED' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Notes</label>
                      <textarea
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        rows={3}
                        placeholder="Describe how the complaint was resolved..."
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-[#004D40] resize-none"
                      />
                    </div>
                  )}

                  {/* Rejection Reason (shown when rejected) */}
                  {newStatus === 'REJECTED' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason</label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        placeholder="Explain why the complaint was rejected..."
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-[#004D40] resize-none"
                      />
                    </div>
                  )}

                  {/* Update Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Update Message (Optional)</label>
                    <textarea
                      value={updateMessage}
                      onChange={(e) => setUpdateMessage(e.target.value)}
                      rows={3}
                      placeholder="Add a message for the user about this update..."
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-[#004D40] resize-none"
                    />
                  </div>

                  {/* Public/Private Toggle */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isPublicUpdate}
                      onChange={(e) => setIsPublicUpdate(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-[#004D40] focus:ring-[#004D40]"
                    />
                    <span className="text-sm text-gray-700">
                      Show this update to the user
                    </span>
                  </label>

                  {/* Internal Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Internal Notes (Admin Only)
                    </label>
                    <textarea
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      rows={3}
                      placeholder="Private notes visible only to admins..."
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-[#004D40] resize-none"
                    />
                  </div>

                  <button
                    onClick={handleUpdateStatus}
                    disabled={isSubmitting}
                    className="w-full py-3 bg-[#004D40] text-white rounded-xl font-medium hover:bg-[#003930] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isSubmitting ? 'Updating...' : 'Update Complaint'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-all duration-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Timeline Item Component
function TimelineItem({ icon, iconBg, iconColor, title, subtitle, date, isPublic }: {
  icon: string;
  iconBg: string;
  iconColor: string;
  title: string;
  subtitle?: string;
  date: string;
  isPublic: boolean;
}) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex gap-4">
      <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <i className={`${icon} ${iconColor}`}></i>
      </div>
      <div className="flex-1 pb-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{title}</p>
            {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">{formatDate(date)}</p>
            {!isPublic && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-400 mt-1">
                <i className="ri-lock-line"></i>
                Internal
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
